import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LayoutGrid, Search } from "lucide-react";
import { useDistrict } from "../../utils/DistrictContext";
import { groupLabel, menuGroups, pageAt, readAlertCounts, readRecents, withContext } from "./context";

const COUNT_CLASS = { leak: "mw-c-leak", anomaly: "mw-c-anomaly", backflow: "mw-c-backflow", noflow: "mw-c-noflow" };

/**
 * The Modules menu (design B), in place of the hamburger list: the same
 * header button, labelled with the group of the page on screen ("Reports &
 * Alerts") and grouped by job. Pages are findable by name, a meter ID offers
 * the pages that take one, and Recent brings users back. Ctrl K opens it.
 */
export default function ModulesMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedDistrict } = useDistrict() || {};
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const here = pageAt(location.pathname, location.search);
  const groups = useMemo(menuGroups, []);
  const counts = open ? readAlertCounts(selectedDistrict) : {};
  const recents = open ? readRecents() : [];

  // Ctrl/Cmd K anywhere, and the breadcrumb's group crumb.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("mw2:open-modules", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mw2:open-modules", onOpen);
    };
  }, []);

  // Closes on a click elsewhere or Escape; opens with the search focused.
  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setActive(0);
    window.setTimeout(() => inputRef.current?.focus(), 0);
    const onDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // What a search finds: pages by name, and for a meter ID the pages that
  // open on one meter.
  const text = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!text) return [];
    const pages = groups.flatMap((group) => group.pages.map((page) => ({ ...page, groupLabel: group.label })));
    const found = pages
      .filter((page) => page.label.toLowerCase().includes(text) || page.groupLabel.toLowerCase().includes(text))
      .map((page) => ({ key: page.key, label: page.label, hint: page.groupLabel, to: page.to, state: page.state }));
    if (/^\d{3,}$/.test(text)) {
      const meter = text;
      found.unshift(
        { key: "m-map", label: `Meter ${meter} on the map`, hint: "Digital Twin", to: `/new-dashboard?tab=twin&meter=${meter}` },
        { key: "m-leak", label: `Meter ${meter} in Leak Flow`, hint: "Reports & Alerts", to: withContext("/leak-flow-category", { meter }) },
        { key: "m-anomaly", label: `Meter ${meter} in Anomaly Detection`, hint: "Analyze", to: withContext("/totalflow?tab=anomaly", { meter }) },
        { key: "m-usage", label: `Meter ${meter} in Water Usage`, hint: "Monitor", to: withContext("/waters", { meter }) },
      );
    }
    return found;
  }, [text, groups]);

  const go = (item) => {
    setOpen(false);
    navigate(item.to, item.state ? { state: item.state } : undefined);
  };

  const onSearchKey = (event) => {
    if (!results.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(results[Math.min(active, results.length - 1)]);
    }
  };

  return (
    <div className="mw-modules" ref={rootRef}>
      <button
        type="button"
        className={`mw-modules__btn${open ? " is-open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        title="Modules (Ctrl K)"
        onClick={() => setOpen((value) => !value)}
      >
        <LayoutGrid />
        <span>{here?.group ? groupLabel(here.group) : "Modules"}</span>
        <ChevronDown className="mw-modules__chev" />
      </button>
      {open ? (
        <div className="mw-modules__menu" role="dialog" aria-label="Modules">
          <div className="mw-modules__search">
            <Search />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKey}
              placeholder="Find a page or meter"
              aria-label="Find a page or meter"
            />
            <kbd>Ctrl K</kbd>
          </div>

          {text ? (
            <div className="mw-modules__group" role="listbox">
              {results.length ? (
                results.map((item, index) => (
                  <button
                    key={item.key}
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    className={`mw-modules__item${index === active ? " is-active" : ""}`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(item)}
                  >
                    {item.label}
                    <small>{item.hint}</small>
                  </button>
                ))
              ) : (
                <div className="mw-modules__empty">No page or meter matches “{query.trim()}”.</div>
              )}
            </div>
          ) : (
            <>
              {recents.length ? (
                <div className="mw-modules__recent">
                  <span className="mw-kicker">Recent</span>
                  {recents.map((item) => (
                    <button key={item.label + item.to} type="button" onClick={() => go(item)}>
                      {item.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="mw-modules__groups">
                {groups.map((group) => (
                  <div key={group.id} className="mw-modules__group">
                    <span className="mw-kicker">{group.label}</span>
                    {group.pages.map((page) => (
                      <button
                        key={page.key}
                        type="button"
                        className={`mw-modules__item${here?.key === page.key ? " is-here" : ""}`}
                        aria-current={here?.key === page.key ? "page" : undefined}
                        onClick={() => go(page)}
                      >
                        {page.label}
                        {page.count && counts[page.count] != null ? (
                          <span className={`mw-modules__count ${COUNT_CLASS[page.count]}`}>{counts[page.count].toLocaleString()}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
